import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import Swal from "sweetalert2";
import DataTable from "../components/DataTable";

const moment = require("moment");

const MODULO = "Contratos";

const normalizarTexto = (texto = "") =>
  texto
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");

const ListarContratos = () => {
  const { id } = useParams();

  const [contratos, setContratos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const navigate = useNavigate();

  // ==============================
  // CONFIG TOKEN
  // ==============================
  const authConfig = () => {
    const token = localStorage.getItem("token");

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  // ==============================
  // PERMISOS MÓDULO CONTRATOS
  // ==============================
  const getModuloContratos = () => {
    try {
      const permisosStorage = localStorage.getItem("permisos");
      const usuarioStorage = localStorage.getItem("usuario");
      const userStorage = localStorage.getItem("user");
      const usuariosStorage = localStorage.getItem("usuarios");

      const permisosData = permisosStorage
        ? JSON.parse(permisosStorage)
        : usuarioStorage
          ? JSON.parse(usuarioStorage)
          : userStorage
            ? JSON.parse(userStorage)
            : usuariosStorage
              ? JSON.parse(usuariosStorage)
              : {};

      const modulos = Array.isArray(permisosData)
        ? permisosData
        : permisosData?.modulos ||
        permisosData?.permisos ||
        permisosData?.usuario?.modulos ||
        permisosData?.usuario?.permisos ||
        permisosData?.user?.modulos ||
        permisosData?.user?.permisos ||
        [];

      const moduloBuscado = normalizarTexto(MODULO);

      return (
        modulos.find((modulo) => {
          const nombre =
            modulo?.nombreModulo ||
            modulo?.modulo ||
            modulo?.nombre ||
            "";

          return normalizarTexto(nombre) === moduloBuscado;
        }) || {}
      );
    } catch (error) {
      console.error("Error leyendo permisos:", error);
      return {};
    }
  };

  const moduloContratos = useMemo(() => getModuloContratos(), []);

  const puedeCrear =
    moduloContratos?.puedeCrear === true ||
    moduloContratos?.crear === true ||
    moduloContratos?.create === true;

  const puedeActualizar =
    moduloContratos?.puedeActualizar === true ||
    moduloContratos?.actualizar === true ||
    moduloContratos?.editar === true ||
    moduloContratos?.update === true;

  const puedeEliminar =
    moduloContratos?.puedeEliminar === true ||
    moduloContratos?.eliminar === true ||
    moduloContratos?.delete === true;

  // ==============================
  // VALIDAR SESIÓN
  // ==============================
  const manejarErrorSesion = useCallback(
    (error) => {
      if (error?.response?.status === 401) {
        Swal.fire({
          icon: "warning",
          title: "Sesión expirada",
          text: "Por favor inicia sesión nuevamente.",
          confirmButtonText: "Aceptar",
        }).then(() => {
          localStorage.removeItem("token");
          navigate("/login", { replace: true });
        });

        return true;
      }

      return false;
    },
    [navigate]
  );

  // ==============================
  // ELIMINAR CONTRATO
  // ==============================
  const deleteContrato = useCallback(
    async (idContrato) => {
      if (!puedeEliminar) {
        Swal.fire(
          "Sin permisos",
          "No tiene permisos para eliminar contratos.",
          "warning"
        );
        return;
      }

      try {
        const result = await Swal.fire({
          title: "¿Está seguro?",
          text: "¡No podrá revertir este proceso!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Sí, eliminar",
          cancelButtonText: "Cancelar",
        });

        if (!result.isConfirmed) return;

        setDeletingId(idContrato);

        const { data } = await axios.delete(
          `/api/contratos/delete/${idContrato}`,
          authConfig()
        );

        setContratos((prev) =>
          prev.filter((contrato) => contrato._id !== idContrato)
        );

        Swal.fire({
          icon: "success",
          title: data?.message || "Contrato eliminado correctamente",
          showConfirmButton: false,
          timer: 1500,
        });
      } catch (error) {
        console.error("Error al eliminar contrato:", error);

        if (manejarErrorSesion(error)) return;

        Swal.fire(
          "Error",
          error.response?.data?.message || "No se pudo eliminar el contrato.",
          "error"
        );
      } finally {
        setDeletingId(null);
      }
    },
    [puedeEliminar, manejarErrorSesion]
  );

  // ==============================
  // ACCIONES DEL SELECT
  // ==============================
  const handleAccion = useCallback(
    async (accion, contrato) => {
      if (!accion) return;

      switch (accion) {
        case "agendas":
          navigate(`/instructor/agendas/${contrato._id}`, {
            state: { contrato },
          });
          break;

        case "listar_informes":
          navigate(`/instructor/informecontractual/listar/${contrato._id}`);
          break;

        case "informe":
          navigate(`/instructor/informecontractual/${contrato._id}`);
          break;

        case "obligaciones":
          navigate(`/instructor/obligaciones/${contrato._id}`);
          break;

        case "editar":
          if (!puedeActualizar) {
            Swal.fire(
              "Sin permisos",
              "No tiene permisos para actualizar contratos.",
              "warning"
            );
            return;
          }

          navigate(`/instructor/registrarcontratos/editar/${contrato._id}`, {
            state: { contrato },
          });
          break;

        case "eliminar":
          await deleteContrato(contrato._id);
          break;

        default:
          break;
      }
    },
    [navigate, puedeActualizar, deleteContrato]
  );

  // ==============================
  // OBTENER CONTRATOS
  // ==============================
  const getData = useCallback(async () => {
    try {
      setLoading(true);

      const res = await axios.get(
        `/api/contratos/listbyinstructor/${id}`,
        authConfig()
      );

      if (res.data?.ok) {
        const dataConItem = (res.data.contratos || []).map(
          (contrato, index) => ({
            item: index + 1,
            _id: contrato._id,

            numeroContrato: contrato.contrato || "-",

            fechaInicio: contrato.fechaInicio
              ? moment
                .utc(contrato.fechaInicio)
                .add(5, "hours")
                .format("DD/MM/YYYY")
              : "-",

            fechaFin: contrato.fechaFin
              ? moment
                .utc(contrato.fechaFin)
                .add(5, "hours")
                .format("DD/MM/YYYY")
              : "-",

            valorContrato: `$${Number(
              contrato.valorContrato || 0
            ).toLocaleString("es-CO")}`,

            estado: contrato.estado || "-",

            entidadContratante: contrato.regional?.nombreRegional || "-",

            centroFormacion: contrato.centroFormacion?.nombreCentro || "-",

            funciones: (
              <div className="d-flex justify-content-end">
                <div
                  className="input-group input-group-sm"
                  style={{ width: 260 }}
                >
                  <span className="input-group-text bg-white">
                    <i className="fa-solid fa-gear"></i>
                  </span>

                  <select
                    className="form-select"
                    defaultValue=""
                    disabled={deletingId === contrato._id}
                    onChange={(e) => {
                      const accion = e.target.value;
                      e.target.value = "";
                      handleAccion(accion, contrato);
                    }}
                  >
                    <option value="" disabled>
                      Acciones del contrato…
                    </option>

                    <optgroup label="Gestión">
                      <option value="agendas">✈️ Agendas de Viaje</option>
                      <option value="listar_informes">
                        📋 Informes Contractuales
                      </option>
                      <option value="obligaciones">📑 Obligaciones</option>
                    </optgroup>

                    <optgroup label="Administración">
                      {puedeActualizar && (
                        <option value="editar">✏️ Editar Contrato</option>
                      )}

                      {puedeEliminar && (
                        <option value="eliminar">🗑️ Eliminar Contrato</option>
                      )}
                    </optgroup>
                  </select>
                </div>
              </div>
            ),
          })
        );

        setContratos(dataConItem);
      } else {
        setContratos([]);
      }
    } catch (error) {
      console.error("Error al obtener contratos:", error);

      if (manejarErrorSesion(error)) return;

      Swal.fire(
        "Error",
        error.response?.data?.message || "No se pudieron cargar los contratos.",
        "error"
      );

      setContratos([]);
    } finally {
      setLoading(false);
    }
  }, [
    id,
    puedeActualizar,
    puedeEliminar,
    deletingId,
    handleAccion,
    manejarErrorSesion,
  ]);

  useEffect(() => {
    getData();
  }, [getData]);

  // ==============================
  // COLUMNAS
  // ==============================
  const columns = useMemo(
    () => [
      { Header: "Item", accessor: "item" },
      { Header: "N° Contrato", accessor: "numeroContrato" },
      { Header: "Inicio", accessor: "fechaInicio" },
      { Header: "Fin", accessor: "fechaFin" },
      { Header: "Valor ($)", accessor: "valorContrato" },
      { Header: "Estado", accessor: "estado" },
      { Header: "Entidad", accessor: "entidadContratante" },
      { Header: "Centro", accessor: "centroFormacion" },
      { Header: "Acciones", accessor: "funciones" },
    ],
    []
  );

  // ==============================
  // RENDER
  // ==============================
  return (
    <div>
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="fw-semibold mb-1">
                  Contratos del Instructor
                </h5>

                <small className="text-secondary">
                  Gestión contractual, informes y obligaciones asociadas.
                </small>
              </div>

              <div className="d-flex gap-2 flex-wrap">
                <button
                  className="btn btn-outline-dark"
                  type="button"
                  onClick={() => navigate(-1)}
                  title="Volver"
                  disabled={loading}
                >
                  <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                  Volver
                </button>

                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={getData}
                  title="Actualizar"
                  disabled={loading}
                >
                  <i
                    className={`fa-solid ${loading ? "fa-spinner fa-spin" : "fa-rotate"
                      } me-2`}
                  ></i>
                  Actualizar
                </button>

                {puedeCrear && (
                  <button
                    className="btn btn-dark"
                    type="button"
                    disabled={loading}
                    onClick={() => navigate(`/instructor/registrarcontratos/${id}`)}
                    title="Nuevo contrato"
                  >
                    <i className="fa-solid fa-plus me-2"></i>
                    Nuevo
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="p-3 p-md-4">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border" role="status" />
                <div className="mt-3">Cargando contratos…</div>
              </div>
            ) : contratos.length > 0 ? (
              <div className="overflow-hidden">
                <DataTable columns={columns} data={contratos} />
              </div>
            ) : (
              <div className="text-center py-5 text-secondary">
                <div className="display-6 mb-2">📄</div>

                <div className="fw-semibold">
                  No hay contratos registrados
                </div>

                <div className="small mb-3">
                  Este instructor aún no tiene contratos asociados.
                </div>

                {puedeCrear && (
                  <button
                    className="btn btn-dark"
                    type="button"
                    onClick={() => navigate(`/instructor/registrarcontratos/${id}`)}
                  >
                    <i className="fa-solid fa-plus me-2"></i>
                    Registrar contrato
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListarContratos;